import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { capitalCase } from "change-case";
import MDButton from "components/MDButton";
import UiButton from "components/base/UiButton";
import { JobBillingStatusValue } from "./quickbooksSyncUtils";
import { quickbooksCancelButtonSx } from "./quickbooksSyncStyles";

type Props = {
  availableBillingStatuses: JobBillingStatusValue[];
  jobBillingStatus: JobBillingStatusValue;
  loading: boolean;
  onCancel?: () => void;
  onConfirm: () => void;
  onPreview: () => void;
  setJobBillingStatus: (value: JobBillingStatusValue) => void;
  setSyncEstimate: (value: boolean) => void;
  setSyncInvoice: (value: boolean) => void;
  setSyncJob: (value: boolean) => void;
  syncEstimate: boolean;
  syncInvoice: boolean;
  syncJob: boolean;
};

export default function QuickbooksSyncFooter({
  availableBillingStatuses,
  jobBillingStatus,
  loading,
  onCancel,
  onConfirm,
  onPreview,
  setJobBillingStatus,
  setSyncEstimate,
  setSyncInvoice,
  setSyncJob,
  syncEstimate,
  syncInvoice,
  syncJob,
}: Props) {
  const confirmLabel = syncInvoice
    ? "Create Invoice"
    : syncEstimate
    ? "Create Estimate"
    : "Confirm Sync";

  return (
    <Paper
      elevation={8}
      sx={{
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        mt: "auto",
        flexShrink: 0,
        px: { xs: 1.5, md: 2 },
        py: 1.25,
        borderTop: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={1.25}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
            QuickBooks targets
          </Typography>
          <FormGroup row>
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncInvoice}
                  onChange={(e) => setSyncInvoice(e.target.checked)}
                />
              }
              label="Invoice"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncEstimate}
                  onChange={(e) => setSyncEstimate(e.target.checked)}
                />
              }
              label="Estimate"
            />
            <FormControlLabel
              control={
                <Checkbox checked={syncJob} onChange={(e) => setSyncJob(e.target.checked)} />
              }
              label="Job"
            />
          </FormGroup>
        </Box>

        <Box sx={{ minWidth: { xs: "100%", sm: 210 } }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            Job Billing Status
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={jobBillingStatus}
              onChange={(e) => setJobBillingStatus(e.target.value as JobBillingStatusValue)}
            >
              {availableBillingStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {capitalCase(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Stack direction="row" spacing={1} justifyContent={{ xs: "flex-end", lg: "flex-start" }}>
          <UiButton uiVariant="primary-grey" disabled={loading} onClick={onPreview}>
            Preview Sync
          </UiButton>
          <MDButton
            color="light"
            disabled={loading}
            onClick={onCancel}
            sx={quickbooksCancelButtonSx}
          >
            Cancel
          </MDButton>
          <MDButton color="success" disabled={loading} onClick={onConfirm}>
            {confirmLabel}
          </MDButton>
        </Stack>
      </Stack>
    </Paper>
  );
}
